import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { useRcmImport } from "../../../contexts/rcmImportContext";
import type { ControlCandidate, RcmMapping } from "../../../lib/rcmParser";
import { toast } from "sonner";
import { ConfiguredState, DryControl } from "../../../models/audit/control";
import { useAuth } from "../../../contexts/authContext";
import { fetchFastApi } from "../../../api/fastapi";

type FieldKey = keyof Required<RcmMapping>;

const FIELDS: {
  key: FieldKey;
  label: string;
  helper: string;
  required: boolean;
}[] = [
  {
    key: "controlId",
    label: "Control ID",
    helper: "Unique identifier for each control",
    required: true,
  },
  {
    key: "controlDescription",
    label: "Control Description",
    helper: "Short name or title of the control",
    required: false,
  },
  {
    key: "processSystem",
    label: "Process/System",
    helper: "Business process or IT system name",
    required: false,
  },
  {
    key: "cycle",
    label: "Cycle",
    helper: "Cycle or control category",
    required: false,
  },
  {
    key: "controlOwner",
    label: "Control Owner",
    helper: "Owner or responsible party",
    required: false,
  },
];

const NONE_VALUE = "__none__";

export const MapRcmColumnsPage = () => {
  const navigate = useNavigate();
  const { parsed, mapping, setMapping, candidates } = useRcmImport();
  const [submitting, setSubmitting] = useState(false);
  const { workspace, user } = useAuth();

  useEffect(() => {
    if (!parsed) {
      navigate("/audit/rcm/upload", { replace: true });
    }
  }, [parsed, navigate]);

  const headers = useMemo(() => parsed?.headers || [], [parsed]);

  const mappingComplete = useMemo(() => {
    return FIELDS.filter((f) => f.required).every((f) => !!mapping[f.key]);
  }, [mapping]);

  const onGenerate = async () => {
    if (!mappingComplete) {
      toast.error("Please map all required fields before continuing");
      return;
    }
    if (!candidates || candidates.length === 0) {
      toast.error("No rows found to import");
      return;
    }
    setSubmitting(true);

    try {
      const controls: DryControl[] = [];
      for (const c of candidates) {
        const newControl: DryControl = {
          id: crypto.randomUUID(),
          workspaceId: workspace?.id || "",
          createdBy: user?.id || "",
          name: c.controlId || "",
          description: c.controlDescription || "",
          controlOwner: c.controlOwner || "",
          controlType: "itgc",
          isTemplate: true,
          ipeProcedureId: null,
          duplicatedFrom: null,
          cycle: c.cycle || "",
          process: c.processSystem || "",
          configuredState: ConfiguredState.NotConfigured,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        controls.push(newControl);
      }
      const response = await fetchFastApi({
        path: "/controls/v2/bulk-import-controls",
        method: "POST",
        body: {
          controls,
        },
      });
      toast.success(
        `Imported ${response.totalCount} control${
          response.totalCount === 1 ? "" : "s"
        }`,
      );
      navigate("/audit/controls/v2");
    } catch (e) {
      console.error(e);
      toast.error("Failed to import controls");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-screen w-full overflow-y-auto">
      <div className="p-10 max-w-[1100px] mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight">
            Map Your Columns
          </h1>
          <p className="text-stone-600 mt-2 max-w-2xl">
            We've auto-detected potential matches between your uploaded columns
            and our required fields. Review each mapping below and adjust if
            needed. Each required field must be mapped to a unique column.
          </p>
        </div>

        {/* Mappings */}
        <div className="border rounded-md p-4 mb-6">
          <div className="text-sm font-medium mb-3">Field Mappings</div>
          <div className="grid grid-cols-1 gap-4">
            {FIELDS.map((field) => (
              <div key={field.key} className="grid grid-cols-1 gap-2">
                <div className="text-sm font-medium">
                  {field.label}
                  {field.required && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
                  <div className="md:col-span-2">
                    <Select
                      value={
                        (mapping[field.key] as string) ||
                        (field.required ? undefined : NONE_VALUE)
                      }
                      onValueChange={(v) =>
                        setMapping({
                          [field.key]: v === NONE_VALUE ? undefined : v,
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            field.required
                              ? "Select a column"
                              : "Select a column or None"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {!field.required && (
                          <SelectItem value={NONE_VALUE}>None</SelectItem>
                        )}
                        {headers.map((h) => (
                          <SelectItem key={h} value={h}>
                            {h}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {mapping[field.key] ? (
                      <div className="text-xs text-green-700 mt-1">
                        Mapped successfully
                      </div>
                    ) : (
                      <div className="text-xs text-stone-500 mt-1">
                        {field.helper}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div className="border rounded-md overflow-hidden">
          <div className="p-3 border-b text-sm font-medium bg-white">
            Preview with Mapping Applied
          </div>
          <div className="relative max-h-[calc(100vh-300px)] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b">
                  <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground w-[160px] bg-white">
                    Control ID
                  </th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground w-[340px] bg-white">
                    Control Description
                  </th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground w-[200px] bg-white">
                    Process/System
                  </th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground w-[200px] bg-white">
                    Cycle
                  </th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground w-[200px] bg-white">
                    Control Owner
                  </th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((r: ControlCandidate, idx: number) => (
                  <tr
                    key={idx}
                    className="border-b transition-colors hover:bg-muted/50"
                  >
                    <td className="p-4 align-middle">{r.controlId || "-"}</td>
                    <td className="p-4 align-middle">
                      {r.controlDescription || "-"}
                    </td>
                    <td className="p-4 align-middle text-sm text-stone-700">
                      {r.processSystem || "-"}
                    </td>
                    <td className="p-4 align-middle text-sm text-stone-700">
                      {r.cycle || "-"}
                    </td>
                    <td className="p-4 align-middle text-sm text-stone-700">
                      {r.controlOwner || "-"}
                    </td>
                  </tr>
                ))}
                {candidates.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center py-8 text-stone-500 text-sm"
                    >
                      No preview rows available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button
            variant="outline"
            onClick={() => navigate("/audit/rcm/upload")}
          >
            Back
          </Button>
          <Button
            onClick={onGenerate}
            disabled={!mappingComplete || submitting}
            loading={submitting}
          >
            Generate Controls
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MapRcmColumnsPage;
